import axios from 'axios';
import { Helpers } from '../helpers/Helpers';
import { ColumnChooserModel } from '../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../models/common/ColumnFilterModel';

/**
 * QR Noktaları (İnsan Kaynakları > Tanımlamalar, 11.10.2026).
 * Depoya asılan basılı QR; personel mesai başı / sonu için telefonuyla okutur.
 */

export const FATURA_ATAMALARI = [
    { value: 1, label: 'Sabit' },
    { value: 2, label: 'Oranlı dağıtım' },
    { value: 3, label: 'Back office atar' },
];

export const faturaAtamasiAdi = (deger: number | undefined | null) => FATURA_ATAMALARI.find(x => x.value === deger)?.label ?? '';

/**
 * QR noktası türü (QR Mesai Faz 2, 18.10.2026): Depo'da iki QR (Lojistik = qrKodu, Ambalaj = qrKoduAmbalaj),
 * Ofis'te tek QR (iş tipi yok).
 */
export const NOKTA_TURLERI = [
    { value: 1, label: 'Depo' },
    { value: 2, label: 'Ofis' },
];

export const noktaTuruAdi = (deger: number | undefined | null) => NOKTA_TURLERI.find(x => x.value === deger)?.label ?? '';

export interface MesaiNoktasiDagitim {
    companyPoId: string
    oran: number
    etiket?: string
    customerName?: string
    divisionName?: string
    poName?: string
    poNo?: string
}

export interface MesaiNoktasiItem {
    mesaiNoktasiId: string
    ad: string
    lat: number | null
    lon: number | null
    yaricap: number
    faturaAtamasi: number
    faturaAtamasiAdi: string
    companyPoId: string | null
    poEtiketi: string | null
    dagitimlar: MesaiNoktasiDagitim[]
    dagitimOzeti: string | null
    toplamOran: number
    /** 1 Depo, 2 Ofis (Faz 2; eski kayıtlar Depo). */
    tur: number
    turAdi: string
    /** Depo'da Lojistik QR'ı; Ofis'te tek QR. */
    qrKodu: string
    qrMetni: string
    /** Yalnız Depo: Ambalaj QR'ı ("KXQR:N:{kod}"); Ofis'te null. */
    qrKoduAmbalaj: string | null
    qrMetniAmbalaj: string | null
    isCustomActive: boolean
    createdDate: string
    createdUser: string
    updatedDate: string
    updatedUser: string
    /** Mesai Talepleri eşleşmesi (#507): yetkili personel, müşteri ve bölüm (bölüm boş = müşterinin bütün bölümleri). */
    yetkiliPersoneller?: { personnelId: string, ad: string }[]
    musteriler?: { musteriId: string, ad?: string | null }[]
    bolumler?: { musteriId: string, bolumId: string, ad?: string | null }[]
    yetkiliOzeti?: string | null
    musteriOzeti?: string | null
    /** Liste ekranında satır seçimi (yalnız ekranda). */
    isSelected?: boolean
}

/** Standart liste ekranı isteği: sayfalama, sıralama, kolon filtreleri, Excel. */
export interface MesaiNoktasiListIstegi {
    searchTerm: string
    skip: number
    take: number
    currentPage: number
    sortColumn?: string
    sortDirection?: string
    columnChooser: ColumnChooserModel[] | undefined
    columnFilters: ColumnFilterModel[]
    ids?: string[]
}

export const initialMesaiNoktasiListIstegi: MesaiNoktasiListIstegi = {
    searchTerm: '',
    skip: 0,
    take: 50,
    currentPage: 1,
    sortColumn: undefined,
    sortDirection: undefined,
    columnChooser: undefined,
    columnFilters: []
}

export interface MesaiNoktasiListSonucu {
    list: MesaiNoktasiItem[]
    totalRecordCount: number
}

export interface MesaiNoktasiPoSatiri {
    companyPoId: string
    customerId?: string | null
    customerName?: string | null
    divisionId?: string | null
    divisionName?: string | null
    poName?: string | null
    poNo?: string | null
    etiket: string
}

export interface KaydetMesaiNoktasiIstegi {
    mesaiNoktasiId?: string | null
    ad: string
    /** 1 Depo, 2 Ofis. Depo kaydedilirken Ambalaj QR'ı yoksa sunucu üretir; Ofis'te silinir. */
    tur: number
    lat: number | null
    lon: number | null
    yaricap: number
    faturaAtamasi: number
    companyPoId: string | null
    isCustomActive: boolean
    dagitimlar: { companyPoId: string, oran: number }[]
    /** #507: gönderilmezse (undefined) sunucu mevcut listeyi korur. */
    yetkiliPersonelIdleri?: string[]
    musteriIdleri?: string[]
    bolumler?: { musteriId: string, bolumId: string }[]
}

interface Yanit<T> { isSuccess: boolean, message?: string, data: T }

const cagir = async <T>(uc: string, govde: any = {}): Promise<Yanit<T>> => {
    try {
        console.log('SERVICE CALL - MesaiNoktasi/' + uc, JSON.stringify(govde));
        const resp = await axios.post(Helpers.getEndpoint() + '/MesaiNoktasi/' + uc, govde);
        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

export class MesaiNoktasiService {
    static getListPagingAndFilter = (istek: MesaiNoktasiListIstegi) => cagir<MesaiNoktasiListSonucu>('GetListPagingAndFilter', istek);
    static exportList = (istek: MesaiNoktasiListIstegi) => cagir<string>('ExportList', istek);
    static getById = (id: string) => cagir<MesaiNoktasiItem>('GetById', { id });
    static kaydet = (istek: KaydetMesaiNoktasiIstegi) => cagir<MesaiNoktasiItem>('Kaydet', istek);
    static sil = (ids: string[]) => cagir<boolean>('Sil', { ids });
    /** Varsayılan: noktanın iki QR'ı da (Depo'da Lojistik + Ambalaj) yenilenir; ambalaj = true ile yalnız Ambalaj QR'ı. */
    static qrYenile = (id: string, ambalaj: boolean = false) => cagir<MesaiNoktasiItem>('QrYenile', ambalaj ? { id, ambalaj: true } : { id });
    static poSatirlari = () => cagir<MesaiNoktasiPoSatiri[]>('PoSatirlari');
    /** Yetkili Personeller seçenekleri: Personel Tanımları'ndaki aktif personel (#507). */
    static personelSecenekleri = () => cagir<{ personnelId: string, ad?: string | null }[]>('NoktaPersonelSecenekleri');
}
